/**
 * Participant credentials, keyed per session (docs/ARCHITECTURE.md §2).
 * Tokens live only here and in the WebSocket `hello` frame; never in a URL.
 *
 * Storage policy: sessionStorage is authoritative for the current tab, so several tabs in one browser can hold
 * different identities (a laptop host plus a display screen, or multi-client testing). localStorage keeps the
 * last identity used for the session in this browser so a closed-and-reopened tab recovers the same participant.
 */
import { z } from 'zod';
import { ParticipantRole } from '@momentum/shared';

const KEY_PREFIX = 'momentum:session:';
const HOST_KEY_PREFIX = 'momentum:host:';
export const HOST_SESSION_KEY = 'momentum:host-session';

function storageKey(sessionId: string): string {
  return `${KEY_PREFIX}${sessionId}`;
}

function hostStorageKey(sessionId: string): string {
  return `${HOST_KEY_PREFIX}${sessionId}`;
}

export const StoredCredentials = z.object({
  participantId: z.string(),
  token: z.string(),
  role: ParticipantRole,
  isHost: z.boolean(),
});
export type StoredCredentials = z.infer<typeof StoredCredentials>;

function readFrom(storage: Storage | undefined, key: string): StoredCredentials | null {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const parsed = StoredCredentials.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function storages(): { tab: Storage | undefined; browser: Storage | undefined } {
  return {
    tab: typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
    browser: typeof localStorage === 'undefined' ? undefined : localStorage,
  };
}

/** Returns null when nothing is stored, storage is unavailable, or the stored value is malformed. */
export function loadCredentials(sessionId: string): StoredCredentials | null {
  const key = storageKey(sessionId);
  const { tab, browser } = storages();
  const tabCredentials = readFrom(tab, key);
  const browserCredentials = readFrom(browser, key);
  try {
    // Entering through the explicit private-host flow makes the browser's host identity authoritative for
    // this room, even if an older guest identity remains in this tab's sessionStorage.
    if (browser?.getItem(HOST_SESSION_KEY) === sessionId) {
      const hostCredentials = loadHostCredentials(sessionId);
      if (hostCredentials) return hostCredentials;
    }
  } catch {
    // Fall back to the usual per-tab identity when storage is unavailable.
  }
  return tabCredentials ?? browserCredentials;
}

/** Loads the durable host identity for a room, including credentials written before host slots existed. */
export function loadHostCredentials(sessionId: string): StoredCredentials | null {
  const { browser } = storages();
  const hostCredentials = readFrom(browser, hostStorageKey(sessionId));
  if (hostCredentials?.isHost) return hostCredentials;

  const legacyCredentials = readFrom(browser, storageKey(sessionId));
  return legacyCredentials?.isHost ? legacyCredentials : null;
}

export function saveCredentials(sessionId: string, credentials: StoredCredentials): void {
  const key = storageKey(sessionId);
  const value = JSON.stringify(credentials);
  const { tab, browser } = storages();
  for (const storage of [tab, browser]) {
    try {
      storage?.setItem(key, value);
    } catch {
      // Storage unavailable (private mode, quota); the session still works for this page load.
    }
  }
  if (credentials.isHost) {
    try {
      browser?.setItem(hostStorageKey(sessionId), value);
    } catch {
      // Storage unavailable (private mode, quota); the session still works for this page load.
    }
  }
}

export function clearCredentials(sessionId: string, expectedToken?: string): void {
  const key = storageKey(sessionId);
  const { tab, browser } = storages();
  const token = expectedToken ?? loadCredentials(sessionId)?.token;
  for (const [storage, candidateKey] of [
    [tab, key],
    [browser, key],
    [browser, hostStorageKey(sessionId)],
  ] as const) {
    try {
      const credentials = readFrom(storage, candidateKey);
      if (!token || credentials?.token === token) storage?.removeItem(candidateKey);
    } catch {
      // Nothing to do if storage is unavailable.
    }
  }
}
